import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { getLatestServerStats, getServerStatsHistory } from 'services/admin/serverServices';
import { Loader } from 'rsuite';
import moment from 'moment';
import './PanelServerStatsScreen.css';
import { FilterBar } from 'components';

const PanelServerStatsScreen = () => {
  const [stats, setStats] = useState({});
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Función para agrupar datos por intervalos y calcular promedios
  const aggregateData = (data, intervalMinutes = 5) => {
    if (!data || data.length === 0) return [];
    
    const grouped = {};
    
    data.forEach(item => {
      const time = moment(item.createdAt);
      const roundedTime = time.clone().startOf('minute').subtract(time.minute() % intervalMinutes, 'minutes');
      const key = roundedTime.format('YYYY-MM-DD HH:mm');
      
      if (!grouped[key]) {
        grouped[key] = {
          createdAt: roundedTime.toDate(),
          cpu: [],
          ram_usage: [],
          disk_usage: []
        };
      }
      
      grouped[key].cpu.push(parseFloat(item.cpu) || 0);
      grouped[key].ram_usage.push(parseFloat(item.ram_usage) || 0);
      grouped[key].disk_usage.push(parseFloat(item.disk_usage) || 0);
    });
    
    return Object.values(grouped).map(group => ({
      createdAt: group.createdAt,
      cpu: group.cpu.reduce((a, b) => a + b, 0) / group.cpu.length,
      ram_usage: group.ram_usage.reduce((a, b) => a + b, 0) / group.ram_usage.length,
      disk_usage: group.disk_usage.reduce((a, b) => a + b, 0) / group.disk_usage.length
    })).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  };

  // Obtener el registro más reciente
  useEffect(() => {
    getLatestServerStats().then((response) => setStats(response))
    const interval = setInterval(async () => {
      const stats = await getLatestServerStats();
      setStats(stats);
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  // useEffect(() => {
  //   setLoading(true);
  //   getLatestServerStats()
  //     .then((response) => {
  //       if (response) {
  //         setStats(response);
  //         setError(null);
  //       } else {
  //         setError('Error al obtener estadísticas del servidor');
  //       }
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       setError('Error al obtener estadísticas del servidor');
  //       setLoading(false);
  //     });
  // }, []);

  // Obtener histórico cuando cambia el rango de fechas

  const handlerSubmit = (data) => {
    setHistoryLoading(true);
    getServerStatsHistory(data).then((response) => {
      setHistoryData(response);
    }).finally(() => setHistoryLoading(false));
  };

  // if (error) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
  //         <Typography color="error">{error}</Typography>
  //       </Paper>
  //     </Box>
  //   );
  // }

  // if (loading || !stats) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Loader />
  //     </Box>
  //   );
  // }
  // Determinar el intervalo de agrupación basado en la cantidad de datos
  const getAggregatedData = () => {
    if (historyData.length <= 50) return historyData; // Sin agrupación si hay pocos datos
    if (historyData.length <= 200) return aggregateData(historyData, 5); // Cada 5 minutos
    if (historyData.length <= 500) return aggregateData(historyData, 10); // Cada 10 minutos
    return aggregateData(historyData, 15); // Cada 15 minutos
  };

  const chartData = getAggregatedData();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Estadísticas del Servidor
      </Typography>
      {/* Información General */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ minHeight: 170 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                CPU
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {stats.cpu?.toFixed(2)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parseFloat(stats.cpu)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: parseFloat(stats.cpu) > 80 ? '#d32f2f' : '#2e7d32'
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ minHeight: 170 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                RAM
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {stats.ram_total} GB
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Usada: {stats.ram_used} GB / Libre: {stats.ram_free} GB
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {stats.ram_usage?.toFixed(2)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parseFloat(stats.ram_usage)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: parseFloat(stats.ram_usage) > 80 ? '#d32f2f' : '#2e7d32'
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ minHeight: 170 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Disco
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {(stats.disk_total / (1024 ** 3)).toFixed(2)} GB
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Usada: {(stats.disk_used / (1024 ** 3)).toFixed(2)} GB / Libre: {(stats.disk_available / (1024 ** 3)).toFixed(2)} GB
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {stats.disk_usage?.toFixed(2)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parseFloat(stats.disk_usage)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: parseFloat(stats.disk_usage) > 80 ? '#d32f2f' : '#2e7d32'
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <FilterBar
        showTitle={false}
        handlerFilter={handlerSubmit}
        defaultValues={{
          date: [
            moment().subtract(1, 'hour').toDate(),
            moment().toDate(),
          ],
        }}
        filters={[
          {
            type: "hourRange",
            name: "date",
            label: "Horas",
          },
        ]}
      />
      {/* Gráficas de tendencia */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Uso de CPU" />
            <CardContent>
              {historyLoading ? <Loader /> : (
                <LineChart
                  height={300}
                  dataset={chartData}
                  xAxis={[
                    { dataKey: 'createdAt', valueFormatter: (value) => moment(value).format('HH:mm') },
                  ]}
                  yAxis={[{ width: 50, valueFormatter: (value) => `${value}%` }]}
                  series={[{ curve: "linear", dataKey: 'cpu', color: '#1976d2' }]}
                  grid={{ vertical: true, horizontal: true }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Uso de RAM" />
            <CardContent>
              {historyLoading ? <Loader /> : (
                <LineChart
                  height={300}
                  dataset={chartData}
                  xAxis={[
                    { dataKey: 'createdAt', valueFormatter: (value) => moment(value).format('HH:mm') },
                  ]}
                  yAxis={[{ width: 50, valueFormatter: (value) => `${value}%`  }]}
                  series={[{ curve: "linear", dataKey: 'ram_usage', color: '#19d254' }]}
                  grid={{ vertical: true, horizontal: true }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Uso de Disco" />
            <CardContent>
              {historyLoading ? <Loader /> : (
                  <LineChart
                  height={300}
                  dataset={chartData}
                  xAxis={[
                    { dataKey: 'createdAt', valueFormatter: (value) => moment(value).format('HH:mm') },
                  ]}
                  yAxis={[{ width: 50, valueFormatter: (value) => `${value}%` }]}
                  series={[{ curve: "linear", dataKey: 'disk_usage', color: '#FF9800' }]}
                  grid={{ vertical: true, horizontal: true }}
                />
                )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PanelServerStatsScreen;
